import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchProductPriceChanges,
  fetchProducts,
  syncProducts,
  updateProductQuantity,
} from '../../api/products';
import { useLatestSyncJob, waitForSyncJob } from '../../api/syncJobs';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { invalidateCache, useCachedResource } from '../../hooks/useCachedResource';
import {
  Badge,
  EmptyState,
  SyncRunBanner,
  StatusBanner,
  PageLoading,
  inputClassName,
  panelClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
  tableCellClassName,
  tableHeadClassName,
} from '../../components/ui';
import { ProductThumb } from '../../components/ui/ProductThumb';
import { CopyableValue } from '../../components/ui/CopyableValue';

const RECENT_PRICE_DAYS = 7;

function currencyLabel(marketplace) {
  return marketplace === 'US' ? 'USD' : 'INR';
}

function formatMoney(marketplace, value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }
  return `${currencyLabel(marketplace)} ${Number(value).toFixed(2)}`;
}

function formatRelativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function isRecentPriceChange(item, withinDays = RECENT_PRICE_DAYS) {
  if (!item?.priceChangedAt) return false;
  const changed = new Date(item.priceChangedAt).getTime();
  if (Number.isNaN(changed)) return false;
  return Date.now() - changed <= withinDays * 24 * 60 * 60 * 1000;
}

function ListingPriceCell({ item }) {
  const currency = currencyLabel(item.marketplace);
  if (item.price === null || item.price === undefined) {
    return <span className="text-[rgb(var(--crm-text-muted))]">—</span>;
  }

  const showChange =
    item.previousPrice !== null &&
    item.previousPrice !== undefined &&
    Number(item.previousPrice) !== Number(item.price) &&
    isRecentPriceChange(item);

  return (
    <div className="grid gap-0.5">
      <span className="font-medium tabular-nums text-[rgb(var(--crm-text))]">
        {currency} {Number(item.price).toFixed(2)}
      </span>
      {showChange ? (
        <span className="flex flex-wrap items-center gap-1 text-[11px] text-[rgb(var(--crm-text-muted))]">
          <span className="tabular-nums line-through">
            {currency} {Number(item.previousPrice).toFixed(2)}
          </span>
          <Badge tone="warning">Changed</Badge>
          <span>{formatRelativeTime(item.priceChangedAt)}</span>
        </span>
      ) : null}
    </div>
  );
}

function QuantityEditor({ item, token, onUpdated, onError }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(
    item.quantity === null || item.quantity === undefined ? '' : String(item.quantity)
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(item.quantity === null || item.quantity === undefined ? '' : String(item.quantity));
  }, [item.quantity]);

  const saveQuantity = async () => {
    const parsed = Number(value);

    if (!Number.isInteger(parsed) || parsed < 0) {
      onError('Quantity must be a whole number greater than or equal to 0.');
      return;
    }

    if (parsed === item.quantity) {
      setEditing(false);
      return;
    }

    setSaving(true);

    try {
      const payload = await updateProductQuantity(token, item.id, parsed);
      onUpdated(payload.item, payload.message, payload.pending);
      setEditing(false);
    } catch (error) {
      onError(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (!item.sellerSku) {
    return <span className="text-[rgb(var(--crm-text-muted))]">—</span>;
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="inline-flex min-w-[3rem] items-center justify-center rounded-md border border-transparent px-2 py-1 font-medium text-[rgb(var(--crm-text))] transition hover:border-[rgb(var(--crm-border))] hover:bg-[rgb(var(--crm-page-bg))]"
        title="Click to edit quantity on Amazon"
      >
        {item.quantity === null || item.quantity === undefined ? 'Set qty' : item.quantity}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        min="0"
        step="1"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            saveQuantity();
          }
          if (event.key === 'Escape') {
            setValue(
              item.quantity === null || item.quantity === undefined ? '' : String(item.quantity)
            );
            setEditing(false);
          }
        }}
        className={`${inputClassName} w-24`}
        autoFocus
        disabled={saving}
      />
      <button
        type="button"
        className={primaryButtonClassName}
        onClick={saveQuantity}
        disabled={saving}
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
      <button
        type="button"
        className={secondaryButtonClassName}
        onClick={() => {
          setValue(
            item.quantity === null || item.quantity === undefined ? '' : String(item.quantity)
          );
          setEditing(false);
        }}
        disabled={saving}
      >
        Cancel
      </button>
    </div>
  );
}

const PAGE_SIZE = 50;

function ProductsPage({ token, user }) {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    marketplace: '',
    search: '',
    recentlyChanged: false,
  });
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, 350);
  const [status, setStatus] = useState({ type: 'ready', message: '' });
  const [syncing, setSyncing] = useState(false);
  const [priceChanges, setPriceChanges] = useState([]);
  const [priceChangesLoading, setPriceChangesLoading] = useState(false);
  const hasSellerId = Boolean(user?.spApiSellerId);
  const {
    job: lastSyncJob,
    loading: lastSyncLoading,
    setJob: setLastSyncJob,
    inFlight: syncJobInFlight,
  } = useLatestSyncJob(token, 'products');
  const syncBusy = syncing || syncJobInFlight;

  const cacheKey = useMemo(
    () =>
      `products|${page}|${filters.marketplace || ''}|${filters.search.trim()}|${filters.recentlyChanged ? 'recent' : 'all'}|${PAGE_SIZE}`,
    [page, filters.marketplace, filters.search, filters.recentlyChanged]
  );

  const fetcher = useCallback(async () => {
    const params = {
      page: String(page),
      pageSize: String(PAGE_SIZE),
    };
    if (filters.marketplace) params.marketplace = filters.marketplace;
    if (filters.search.trim()) params.search = filters.search.trim();
    if (filters.recentlyChanged) {
      params.recentlyChanged = '1';
      params.priceChangedWithinDays = String(RECENT_PRICE_DAYS);
    }
    return fetchProducts(token, params);
  }, [token, page, filters.marketplace, filters.search, filters.recentlyChanged]);

  const { data, setData, loading, refreshing, reload, error } = useCachedResource(
    token ? cacheKey : null,
    fetcher,
    { staleTime: 90_000 }
  );

  const loadPriceChanges = useCallback(async () => {
    if (!token) return;
    setPriceChangesLoading(true);
    try {
      const params = { limit: '12' };
      if (filters.marketplace) params.marketplace = filters.marketplace;
      const payload = await fetchProductPriceChanges(token, params);
      setPriceChanges(Array.isArray(payload?.items) ? payload.items : []);
    } catch {
      setPriceChanges([]);
    } finally {
      setPriceChangesLoading(false);
    }
  }, [token, filters.marketplace]);

  useEffect(() => {
    void loadPriceChanges();
  }, [loadPriceChanges]);

  const items = data?.items || [];
  const pagination = data?.pagination || {
    page,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  };

  useEffect(() => {
    if (syncing) {
      return;
    }
    if (loading && !items.length) {
      setStatus({ type: 'loading', message: '' });
      return;
    }
    if (error && !items.length) {
      setStatus({ type: 'error', message: error.message || 'Unable to load products.' });
      return;
    }
    if (data && status.type === 'loading' && !status.message) {
      setStatus({ type: 'ready', message: '' });
    }
  }, [loading, error, data, items.length, status.type, status.message, syncing]);

  const handleQuantityUpdated = (updatedItem, message, pending = false) => {
    setData((current) => {
      if (!current) return current;
      return {
        ...current,
        items: (current.items || []).map((entry) =>
          entry.id === updatedItem.id ? updatedItem : entry
        ),
      };
    });
    setStatus({
      type: pending ? 'info' : 'success',
      message: message || 'Quantity submitted to Amazon.',
    });
  };

  const handleQuantityError = (message) => {
    setStatus({ type: 'error', message });
  };

  const loadProducts = useCallback(
    async ({ force = false } = {}) => {
      try {
        await reload({ force, silent: Boolean(data?.items?.length) && !force });
      } catch {
        // surfaced via error/status effect
      }
    },
    [reload, data]
  );

  useEffect(() => {
    setFilters((current) => {
      if (current.search === debouncedSearch) {
        return current;
      }
      setPage(1);
      return { ...current, search: debouncedSearch };
    });
  }, [debouncedSearch]);

  const handleSync = async () => {
    setSyncing(true);
    setStatus({
      type: 'loading',
      message: 'Starting Amazon product sync in the background…',
    });

    try {
      const started = await syncProducts(token, {
        includeCatalogImages: false,
        backfillImages: true,
      });
      const jobId = started.jobId || started.job?.id;
      if (!jobId) {
        throw new Error(started.message || 'Unable to start product sync.');
      }

      const job = await waitForSyncJob(token, jobId, {
        onUpdate: () => {
          setStatus((prev) => {
            const message = 'Sync running in the background…';
            if (prev?.type === 'loading' && prev.message === message) {
              return prev;
            }
            return { type: 'loading', message };
          });
        },
      });

      setLastSyncJob(job);

      if (job.status === 'failed') {
        setStatus({ type: 'error', message: job.message || job.error || 'Sync failed.' });
        return;
      }

      const warningCount = Array.isArray(job.result?.warnings) ? job.result.warnings.length : 0;
      const priceChangeCount = Number(job.result?.priceChanges ?? 0);
      const priceNote =
        priceChangeCount > 0 && !String(job.message || '').includes('price change')
          ? ` ${priceChangeCount} price change(s) detected.`
          : '';
      setStatus({
        type: warningCount ? 'error' : 'success',
        message:
          (job.message || `Synced ${job.result?.synced ?? 0} listing(s).`) +
          priceNote +
          (warningCount ? ` ${job.result.warnings.join(' ')}` : ''),
      });

      invalidateCache('products|');
      void loadPriceChanges();
      if (page !== 1) {
        setPage(1);
      } else {
        await reload({ force: true });
      }
    } catch (err) {
      setStatus({ type: 'error', message: err.message });
    } finally {
      setSyncing(false);
    }
  };

  const total = pagination.total || 0;
  const totalPages = pagination.totalPages || 0;
  const rangeStart = total === 0 ? 0 : (pagination.page - 1) * pagination.pageSize + 1;
  const rangeEnd = Math.min(pagination.page * pagination.pageSize, total);

  return (
    <div className="grid gap-5">
      <div className={`${panelClassName} overflow-hidden`}>
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[rgb(var(--crm-border))] bg-[linear-gradient(135deg,rgb(var(--crm-surface)),rgb(var(--crm-accent-soft)/0.45))] px-5 py-5">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[rgb(var(--crm-accent))]">
              Catalog
            </p>
            <h2 className="mt-1 text-xl font-semibold tracking-tight text-[rgb(var(--crm-text))]">
              Amazon listings
            </h2>
            <p className="crm-text-muted mt-1 max-w-2xl text-sm leading-relaxed">
              {hasSellerId
                ? 'Live seller catalog from Amazon. Click quantity to push FBM updates (usually reflected in Seller Central within a few minutes).'
                : 'Save your Seller ID in Settings → SP-API, then sync to load listings from Amazon.'}
            </p>
            <div className="mt-3">
              <SyncRunBanner job={lastSyncJob} loading={lastSyncLoading} compact />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={secondaryButtonClassName}
              onClick={() => loadProducts({ force: true })}
              disabled={loading || refreshing || syncBusy}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            <button
              type="button"
              className={primaryButtonClassName}
              onClick={handleSync}
              disabled={syncBusy || !hasSellerId}
              title={
                syncBusy
                  ? 'A sync is already running in the background'
                  : !hasSellerId
                    ? 'Save Seller ID in Settings first'
                    : 'Pull latest listings from Amazon'
              }
            >
              {syncBusy ? 'Syncing…' : 'Sync from Amazon'}
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3 px-5 py-4">
          <label className="grid gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-[rgb(var(--crm-text-muted))]">
              Marketplace
            </span>
            <select
              className={`${inputClassName} min-w-[140px]`}
              value={filters.marketplace}
              onChange={(event) => {
                setPage(1);
                setFilters((current) => ({ ...current, marketplace: event.target.value }));
              }}
            >
              <option value="">All</option>
              <option value="IN">India</option>
            </select>
          </label>

          <label className="grid min-w-[220px] flex-1 gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-[rgb(var(--crm-text-muted))]">
              Search
            </span>
            <input
              className={inputClassName}
              type="search"
              placeholder="SKU, ASIN, or title"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>

          <label className="flex items-center gap-2 pb-2 text-sm text-[rgb(var(--crm-text))]">
            <input
              type="checkbox"
              checked={filters.recentlyChanged}
              onChange={(event) => {
                setPage(1);
                setFilters((current) => ({
                  ...current,
                  recentlyChanged: event.target.checked,
                }));
              }}
              className="h-4 w-4 rounded border-[rgb(var(--crm-border))]"
            />
            <span>Recently changed prices ({RECENT_PRICE_DAYS}d)</span>
          </label>
        </div>
      </div>

      <div className={`${panelClassName} px-5 py-4`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-[rgb(var(--crm-text))]">
              Recent price changes
            </h3>
            <p className="text-xs text-[rgb(var(--crm-text-muted))]">
              Detected when Amazon listing prices differ from the last sync
            </p>
          </div>
          {priceChangesLoading ? (
            <span className="text-xs text-[rgb(var(--crm-text-muted))]">Loading…</span>
          ) : null}
        </div>
        {priceChanges.length ? (
          <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {priceChanges.map((change) => (
              <li
                key={change.id}
                className="rounded-lg border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg))] px-3 py-2.5"
              >
                <p className="truncate text-sm font-medium text-[rgb(var(--crm-text))]">
                  {change.title || change.sellerSku || 'Listing'}
                </p>
                <p className="mt-0.5 truncate text-xs text-[rgb(var(--crm-text-muted))]">
                  {change.sellerSku}
                  {change.asin ? ` · ${change.asin}` : ''}
                </p>
                <p className="mt-1.5 text-sm tabular-nums">
                  <span className="text-[rgb(var(--crm-text-muted))] line-through">
                    {formatMoney(change.marketplace, change.oldPrice)}
                  </span>
                  <span className="mx-1.5 text-[rgb(var(--crm-text-muted))]">→</span>
                  <span className="font-semibold text-[rgb(var(--crm-text))]">
                    {formatMoney(change.marketplace, change.newPrice)}
                  </span>
                </p>
                <p className="mt-1 text-[11px] text-[rgb(var(--crm-text-muted))]">
                  {formatRelativeTime(change.changedAt)}
                </p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-[rgb(var(--crm-text-muted))]">
            {priceChangesLoading
              ? 'Loading recent changes…'
              : 'No price changes recorded yet. Sync after updating prices in Seller Central.'}
          </p>
        )}
      </div>

      <StatusBanner status={status} setStatus={setStatus} />

      {loading && !items.length ? (
        <PageLoading label="Loading listings…" />
      ) : null}

      {!items.length && status.type !== 'loading' ? (
        <EmptyState
          title={filters.recentlyChanged ? 'No recent price changes' : 'No listings yet'}
          description={
            filters.recentlyChanged
              ? `No listings had a price change in the last ${RECENT_PRICE_DAYS} days. Turn off the filter or sync after updating prices in Seller Central.`
              : hasSellerId
                ? 'Your listings will load automatically from Amazon. Use Refresh or Sync if the table is empty.'
                : 'Save your Seller ID in Settings → SP-API, then sync to pull your seller catalog from Amazon.'
          }
          action={
            filters.recentlyChanged ? (
              <button
                type="button"
                className={secondaryButtonClassName}
                onClick={() => {
                  setPage(1);
                  setFilters((current) => ({ ...current, recentlyChanged: false }));
                }}
              >
                Show all listings
              </button>
            ) : (
              <button type="button" className={primaryButtonClassName} onClick={handleSync} disabled={syncBusy}>
                {syncBusy ? 'Syncing…' : 'Sync from Amazon'}
              </button>
            )
          }
        />
      ) : null}

      {items.length ? (
        <div className="grid gap-3">
          <div className="w-full overflow-x-hidden rounded-xl border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] shadow-sm">
            <table className="w-full table-fixed text-left text-sm">
              <thead className={tableHeadClassName}>
                <tr>
                  <th className={`${tableCellClassName} w-[36%]`}>Product</th>
                  <th className={`${tableCellClassName} w-[12%] whitespace-nowrap`}>HSN</th>
                  <th className={`${tableCellClassName} w-[10%] whitespace-nowrap`}>Marketplace</th>
                  <th className={`${tableCellClassName} w-[12%] whitespace-nowrap`}>Status</th>
                  <th className={`${tableCellClassName} w-[10%] whitespace-nowrap`}>Qty</th>
                  <th className={`${tableCellClassName} w-[12%] whitespace-nowrap`}>Price</th>
                  <th className={`${tableCellClassName} w-[8%] whitespace-nowrap`}>Link</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className="border-t border-[rgb(var(--crm-border))] transition hover:bg-[rgb(var(--crm-accent-soft)/0.35)]"
                  >
                    <td className={tableCellClassName}>
                      <div className="flex items-start gap-3">
                        <ProductThumb
                          item={{
                            title: item.title,
                            asinIn: item.marketplace === 'IN' ? item.asin : null,
                            asinCom: item.marketplace === 'US' ? item.asin : null,
                            imageUrl: item.imageUrl,
                          }}
                          marketplace={item.marketplace}
                          token={token}
                        />
                        <div className="min-w-0">
                          <p className="break-words font-medium text-[rgb(var(--crm-text))]">
                            {item.title || 'Untitled listing'}
                          </p>
                          {item.fulfillmentChannel &&
                          String(item.fulfillmentChannel).toUpperCase() !== 'DEFAULT' ? (
                            <p className="text-xs text-[rgb(var(--crm-text-muted))]">
                              {item.fulfillmentChannel}
                            </p>
                          ) : null}
                          <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-[rgb(var(--crm-text-muted))]">
                            {item.sellerSku ? (
                              <CopyableValue label="SKU:" value={item.sellerSku} className="font-medium" />
                            ) : null}
                            {item.sellerSku && item.asin ? <span aria-hidden>·</span> : null}
                            {item.asin ? <CopyableValue label="ASIN:" value={item.asin} /> : null}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className={`${tableCellClassName} whitespace-nowrap`}>
                      {item.hsnCode ? (
                        <CopyableValue label="HSN" value={item.hsnCode} className="font-mono text-xs" />
                      ) : (
                        <span className="text-[rgb(var(--crm-text-muted))]">—</span>
                      )}
                    </td>
                    <td className={`${tableCellClassName} whitespace-nowrap`}>
                      <Badge tone={item.marketplace === 'IN' ? 'info' : 'default'}>
                        {item.marketplace || '—'}
                      </Badge>
                    </td>
                    <td className={`${tableCellClassName} whitespace-nowrap`}>{item.status || '—'}</td>
                    <td className={`${tableCellClassName} whitespace-nowrap`}>
                      <QuantityEditor
                        item={item}
                        token={token}
                        onUpdated={handleQuantityUpdated}
                        onError={handleQuantityError}
                      />
                    </td>
                    <td className={`${tableCellClassName} whitespace-nowrap`}>
                      <ListingPriceCell item={item} />
                    </td>
                    <td className={`${tableCellClassName} whitespace-nowrap`}>
                      {item.productLink ? (
                        <a
                          href={item.productLink}
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium text-[rgb(var(--crm-accent-text))] hover:underline"
                        >
                          View
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] px-4 py-3 text-sm shadow-sm">
            <p className="text-[rgb(var(--crm-text-muted))]">
              Showing <span className="font-medium text-[rgb(var(--crm-text))]">{rangeStart}–{rangeEnd}</span> of{' '}
              <span className="font-medium text-[rgb(var(--crm-text))]">{total.toLocaleString('en-IN')}</span> listing
              {total === 1 ? '' : 's'}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={secondaryButtonClassName}
                disabled={page <= 1 || loading || refreshing}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </button>
              <span className="min-w-[7rem] rounded-lg bg-[rgb(var(--crm-page-bg))] px-3 py-1.5 text-center text-xs font-semibold text-[rgb(var(--crm-text))]">
                Page {totalPages === 0 ? 0 : page} of {totalPages}
              </span>
              <button
                type="button"
                className={secondaryButtonClassName}
                disabled={totalPages === 0 || page >= totalPages || loading || refreshing}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ProductsPage;
